import { useTranslation } from 'react-i18next';
import { FC, ReactNode, useEffect, useState } from 'react';

import { Dropdown, DropdownItem, DropdownVariant } from '@base-app';
import { CardGrid, NoData } from '@builder';
import { COMMON_TRANSLATION } from '@constants/index';
import { getDateWithoutTZ } from '@common/utils';
import { HeaderOption } from '../../../CollectionAndSequencing.types';
import { isArrayEmpty } from '@modules/shared/utils';
import { isUserAdministrator } from '@modules/auth/utils/userRole';
import { SequenceGoal } from '../SequenceGoal.types';
import { STUDY_INSIGHTS_TRANSLATION, colors } from '@modules/study-insights/constants';
import { StyledSequenceGoal, StyledYearlySequenceGoalCard } from './YearlySequenceGoalCard.styles';
import { useAuthStore } from '@modules/auth/store';
import { YearlySequenceGoalCardProps } from './YearlySequenceGoalCard.types';
import { Sizes } from '@common/types';

const YearlySequenceGoalCard: FC<YearlySequenceGoalCardProps> = ({ data, handleEdit }) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const { user: currentUser } = useAuthStore();

  const [percentage, setPercentage] = useState<number>(0);
  const [selectedYear, setSelectedYear] = useState<string>();
  const [sequenceGoal, setSequenceGoal] = useState<SequenceGoal | undefined>();
  const [yearsList, setYearsList] = useState<DropdownItem[]>([]);

  const getHeaderOptions = (): DropdownItem[] | [] => {
    if (currentUser == null || isUserAdministrator(currentUser)) {
      return [
        {
          id: HeaderOption.EDIT,
          label: t('header.options.edit'),
        },
      ];
    } else {
      return [];
    }
  };

  const handleOptionClick = (options: DropdownItem): void => {
    if (options.id === HeaderOption.EDIT) {
      handleEdit();
    }
  };

  const setYear = (year: number): void => {
    setSequenceGoal((data as any[])[year]);
  };

  const handleSelectYear = (value: DropdownItem): void => {
    setSelectedYear(value.label);
    setYear(Number(value.id));
  };

  const renderDropdownYear = (): ReactNode => {
    return (
      <Dropdown
        id="dropdown-year-goal"
        items={yearsList}
        onSelect={handleSelectYear}
        size={Sizes.SMALL}
        title={selectedYear}
        variant={DropdownVariant.PILL}
      />
    );
  };

  const getYears = (): void => {
    const years: DropdownItem[] = [];

    data.forEach((_yearData, index) => {
      years.push({
        id: `${index}`,
        label: `${t('yearly_sequence_goal.year')} ${index + 1}`,
      });
    });

    setYearsList(years);
  };

  useEffect(() => {
    if (data && !isArrayEmpty(data as [])) {
      setYear(0);
      getYears();
    }
  }, [data]);

  useEffect(() => {
    if (yearsList) {
      setSelectedYear(yearsList[0]?.label);
    }
  }, [yearsList]);

  useEffect(() => {
    if (sequenceGoal) {
      const percentage = (100 * sequenceGoal.value ?? 0) / sequenceGoal?.target;

      setPercentage(percentage);
    }
  }, [sequenceGoal]);

  return (
    <CardGrid
      css={{ width: '100%' }}
      title={t('yearly_sequence_goal.title')}
      handleClick={(e) => handleOptionClick(e)}
      headerOptions={getHeaderOptions()}
    >
      <StyledYearlySequenceGoalCard>
        {sequenceGoal != null && selectedYear !== undefined ? (
          <>
            <StyledSequenceGoal
              percentage={percentage}
              accentColor={colors.green200}
              id="yearly-sequence-goal"
              date={`${getDateWithoutTZ(sequenceGoal.startDate)} (${selectedYear}) `}
              dateLabel={t('yearly_sequence_goal.year_start_date')}
              target={sequenceGoal.target}
              targetLabel={t('yearly_sequence_goal.yearly_goal')}
              value={sequenceGoal.value}
            />
            {renderDropdownYear()}
          </>
        ) : (
          <NoData message={t('no_data_available', { ns: COMMON_TRANSLATION })} />
        )}
      </StyledYearlySequenceGoalCard>
    </CardGrid>
  );
};

export default YearlySequenceGoalCard;
